import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppNotification, AppNotificationType } from '@app/models';
import { OrganizationService } from './organization.service';
import { ToastService } from './toast.service';
import { WebsocketService } from './websocket.service';
import { NotificationApiService } from './notification-api.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private readonly MAX_NOTIFICATIONS = 50;
  private readonly DUPLICATE_WINDOW_MS = 5000; // 5 segundos

  private notifications = new BehaviorSubject<AppNotification[]>([]);
  private unreadTotal = new BehaviorSubject<number>(0);
  private subscriptions: Subscription[] = [];
  private recentlyProcessed: Map<string, number> = new Map();
  private isLoading = new BehaviorSubject<boolean>(false);

  public notifications$: Observable<AppNotification[]> =
    this.notifications.asObservable();

  public unreadCount$: Observable<number> = this.unreadTotal.asObservable();

  public isLoading$: Observable<boolean> = this.isLoading.asObservable();

  constructor(
    private websocketService: WebsocketService,
    private organizationService: OrganizationService,
    private router: Router,
    private toastService: ToastService,
    private notificationApiService: NotificationApiService
  ) {
    this.initializeNotificationHandling();
    this.initializeOrganizationChangeListener();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private initializeNotificationHandling(): void {
    const sub = this.websocketService.notificationMessage.subscribe(
      (message: any) => {
        if (message && message.type === 'NOTIFICATION') {
          this.handleIncomingNotification(message.payload);
        }
      }
    );
    this.subscriptions.push(sub);
  }

  private initializeOrganizationChangeListener(): void {
    // Carregar notificações quando a organização estiver disponível
    if (this.organizationService.currentOrganization?.alias) {
      this.loadNotificationsFromApi();
    }

    // Observar quando as permissões são carregadas (indica que a organização mudou)
    const sub = this.organizationService.permissionsLoaded.subscribe(
      loaded => {
        if (loaded && this.organizationService.currentOrganization?.alias) {
          this.loadNotificationsFromApi();
        }
      }
    );
    this.subscriptions.push(sub);
  }

  /**
   * Carrega notificações da API
   */
  public loadNotificationsFromApi(page: number = 0, size: number = 20): void {
    this.isLoading.next(true);

    this.notificationApiService.getNotifications(page, size).subscribe({
      next: response => {
        const notifications = response.content.map(n => ({
          ...n,
          createdAt: new Date(n.createdAt),
          readAt: n.readAt ? new Date(n.readAt) : undefined,
        }));
        this.notifications.next(notifications);
        this.unreadTotal.next(response.unreadTotal);
        this.isLoading.next(false);
      },
      error: err => {
        console.error('Error loading notifications from API:', err);
        this.isLoading.next(false);
      },
    });
  }

  private handleIncomingNotification(notificationData: any): void {
    // Generate ID based on content if not provided
    const notificationId =
      notificationData.id || this.generateContentBasedId(notificationData);

    // Check for duplicates within time window
    if (this.isDuplicateWithinWindow(String(notificationId))) {
      console.log('Duplicate notification ignored:', notificationId);
      return;
    }

    const notification: AppNotification = {
      id: notificationId,
      type: notificationData.type || AppNotificationType.INFO,
      title: notificationData.title || 'Notificação',
      message: notificationData.message || '',
      link: notificationData.link,
      read: false,
      createdAt: new Date(notificationData.createdAt || Date.now()),
      metadata: notificationData.metadata,
    };

    this.addNotification(notification);
  }

  private addNotification(notification: AppNotification): void {
    const currentNotifications = this.notifications.value;

    // Evitar duplicatas
    if (currentNotifications.some(n => n.id === notification.id)) {
      return;
    }

    const updatedNotifications = [notification, ...currentNotifications].slice(
      0,
      this.MAX_NOTIFICATIONS
    );

    this.notifications.next(updatedNotifications);
    // Incrementar contador de não lidas
    this.unreadTotal.next(this.unreadTotal.value + 1);

    // Show toast notification
    this.showNotificationToast(notification);
  }

  private showNotificationToast(notification: AppNotification): void {
    const toastMessage = notification.title;

    if (notification.link) {
      // Clickable toast that navigates to the link
      this.toastService.showClickableToast(
        toastMessage,
        () => {
          this.navigateToNotification(notification);
        },
        {},
        'toast-info'
      );
    } else {
      // Standard toast
      this.toastService.showLight(toastMessage);
    }
  }

  public markAsRead(notificationId: string | number): void {
    const numericId =
      typeof notificationId === 'string'
        ? parseInt(notificationId, 10)
        : notificationId;

    // Atualizar localmente primeiro (otimistic update)
    const currentNotifications = this.notifications.value;
    const notification = currentNotifications.find(n => n.id === notificationId);

    if (notification && !notification.read) {
      const updatedNotifications = currentNotifications.map(n =>
        n.id === notificationId ? { ...n, read: true, readAt: new Date() } : n
      );
      this.notifications.next(updatedNotifications);
      this.unreadTotal.next(Math.max(0, this.unreadTotal.value - 1));

      // Chamar API para persistir
      if (!isNaN(numericId)) {
        this.notificationApiService.markAsRead(numericId).subscribe({
          error: err => {
            console.error('Error marking notification as read:', err);
            // Reverter em caso de erro
            this.notifications.next(currentNotifications);
            this.unreadTotal.next(this.unreadTotal.value + 1);
          },
        });
      }
    }
  }

  public markAllAsRead(): void {
    const currentNotifications = this.notifications.value;
    const hasUnread = currentNotifications.some(n => !n.read);

    if (!hasUnread) {
      return;
    }

    // Atualizar localmente primeiro (otimistic update)
    const updatedNotifications = currentNotifications.map(n => ({
      ...n,
      read: true,
      readAt: n.readAt || new Date(),
    }));
    this.notifications.next(updatedNotifications);
    const previousUnreadCount = this.unreadTotal.value;
    this.unreadTotal.next(0);

    // Chamar API para persistir
    this.notificationApiService.markAllAsRead().subscribe({
      error: err => {
        console.error('Error marking all notifications as read:', err);
        // Reverter em caso de erro
        this.notifications.next(currentNotifications);
        this.unreadTotal.next(previousUnreadCount);
      },
    });
  }

  public removeNotification(notificationId: string | number): void {
    const currentNotifications = this.notifications.value;
    const notification = currentNotifications.find(n => n.id === notificationId);
    const updatedNotifications = currentNotifications.filter(
      n => n.id !== notificationId
    );

    this.notifications.next(updatedNotifications);

    // Atualizar contador se a notificação removida era não lida
    if (notification && !notification.read) {
      this.unreadTotal.next(Math.max(0, this.unreadTotal.value - 1));
    }
  }

  public clearAll(): void {
    this.notifications.next([]);
    this.unreadTotal.next(0);
  }

  public navigateToNotification(notification: AppNotification): void {
    if (!notification.read) {
      this.markAsRead(notification.id);
    }

    if (notification.link) {
      if (notification.link.startsWith('http')) {
        window.open(notification.link, '_blank');
      } else {
        this.router.navigateByUrl(notification.link);
      }
    }
  }

  public getNotifications(): AppNotification[] {
    return this.notifications.value;
  }

  public getUnreadCount(): number {
    return this.unreadTotal.value;
  }

  private generateContentBasedId(data: any): string {
    const content = `${data.title || ''}_${data.message || ''}_${data.type || ''}`;
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return `content_${Math.abs(hash)}`;
  }

  private isDuplicateWithinWindow(id: string): boolean {
    const lastTime = this.recentlyProcessed.get(id);
    const now = Date.now();

    if (lastTime && now - lastTime < this.DUPLICATE_WINDOW_MS) {
      return true;
    }

    this.recentlyProcessed.set(id, now);
    this.cleanupOldEntries();
    return false;
  }

  private cleanupOldEntries(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.recentlyProcessed.forEach((timestamp, key) => {
      if (now - timestamp > this.DUPLICATE_WINDOW_MS * 2) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.recentlyProcessed.delete(key));
  }

  public getIconForType(type: AppNotificationType): string {
    const iconMap: Record<AppNotificationType, string> = {
      [AppNotificationType.INFO]: 'fas fa-info-circle',
      [AppNotificationType.SUCCESS]: 'fas fa-check-circle',
      [AppNotificationType.WARNING]: 'fas fa-exclamation-triangle',
      [AppNotificationType.ERROR]: 'fas fa-times-circle',
      [AppNotificationType.TASK]: 'fas fa-tasks',
      [AppNotificationType.REPORT]: 'fas fa-chart-bar',
    };
    return iconMap[type] || 'fas fa-bell';
  }

  public getColorClassForType(type: AppNotificationType): string {
    const colorMap: Record<AppNotificationType, string> = {
      [AppNotificationType.INFO]: 'text-info',
      [AppNotificationType.SUCCESS]: 'text-success',
      [AppNotificationType.WARNING]: 'text-warning',
      [AppNotificationType.ERROR]: 'text-danger',
      [AppNotificationType.TASK]: 'text-primary',
      [AppNotificationType.REPORT]: 'text-secondary',
    };
    return colorMap[type] || 'text-muted';
  }
}
